// MARK://dhk8/admin-shell/lib/clients/catalog-combos
// Combos del catálogo → dhk8-catalog-combos.
//
//   GET    /v1/combos?tenantId=…&q=…&page=&limit=    → { items: Combo[], total }
//   GET    /v1/combos/:id                            → Combo (con items[])
//   POST   /v1/combos                                → Combo
//   PATCH  /v1/combos/:id                            → Combo
//   DELETE /v1/combos/:id                            → { id } (soft delete)
//   POST   /v1/combos/:id/items                      → ComboItem
//   DELETE /v1/combos/:id/items/:itemId              → { id }
//
// Wrapping: el servicio envuelve sus respuestas en { ok, data } — `apiData`
// las desempaqueta. En PREVIEW_STUB devolvemos data deterministic para que el
// UI corra sin backend.
import { PREVIEW_STUB, mock } from '../env.js'
import { authTenantId } from '../../stores/auth-store.js'
import { gqlClient } from '../gql.js'
import { AdminCombosQuery, AdminComboQuery, AdminCreateComboMutation, AdminUpdateComboMutation, AdminDeleteComboMutation, AdminAddComboItemMutation, AdminRemoveComboItemMutation, AdminUpdateComboItemMutation } from '../queries/combos.queries.js'


export type ComboPricingMode = 'FIXED_PRICE' | 'SUM_WITH_DISCOUNT'
export interface ComboItem {
  id: string
  comboId: string
  // A7 §1.6 — productId XOR categoryId: el item es un producto fijo o un slot por categoría.
  productId: string | null
  categoryId: string | null
  allowedProductIds: string[]
  tenantId: string
  quantity: number
  isOptional: boolean
  sortOrder: number
}
export interface Combo {
  id: string
  tenantId: string
  name: string
  description: string | null
  priceCents: number
  pricingMode: ComboPricingMode
  categoryId: string | null
  imageUrl: string | null
  validFrom: string | null
  validUntil: string | null
  active: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  items: ComboItem[]
}

// SKUs are not in the Combo schema yet — the backend Combo model only carries
// { name, description, priceCents, active }. The admin-shell UI shows a friendly
// "stub" badge for the two known SAVIs stubs the merchant onboarding flagged
// as "necesita configuración": Molletes con queso + Torta. Anything with 0
// items is also surfaced as "necesita configuración".
const STUB_NAMES = new Set(['molletes con queso', 'torta'])
export function isStub(c: Combo): boolean {
  if (c.items.length === 0) return true
  return STUB_NAMES.has(c.name.trim().toLowerCase())
}

// Mapea el Combo del grafo (typed-document-node) al tipo de UI. Los campos son
// 1:1 con el SDL de catalog-combos; normalizamos los nullables.
interface GqlComboShape {
  id: string; tenantId: string; name: string; description?: string | null
  priceCents: number; pricingMode?: string | null; categoryId?: string | null; imageUrl?: string | null
  validFrom?: string | null; validUntil?: string | null
  active: boolean; createdAt: string; updatedAt: string; deletedAt?: string | null
  items: ReadonlyArray<{ id: string; comboId: string; productId?: string | null; categoryId?: string | null; allowedProductIds?: ReadonlyArray<string> | null; quantity: number; isOptional: boolean; sortOrder: number }>
}
function toUiCombo(c: GqlComboShape): Combo {
  return {
    id: c.id, tenantId: c.tenantId, name: c.name, description: c.description ?? null,
    priceCents: c.priceCents, pricingMode: (c.pricingMode as ComboPricingMode) ?? 'FIXED_PRICE',
    categoryId: c.categoryId ?? null, imageUrl: c.imageUrl ?? null,
    validFrom: c.validFrom ?? null, validUntil: c.validUntil ?? null, active: c.active,
    createdAt: c.createdAt, updatedAt: c.updatedAt, deletedAt: c.deletedAt ?? null,
    items: c.items.map((i) => ({
      // ComboItem.tenantId no está en el SDL (es denormalizado del combo padre);
      // lo tomamos del combo para mantener el tipo de UI.
      id: i.id, comboId: i.comboId, productId: i.productId ?? null, categoryId: i.categoryId ?? null,
      allowedProductIds: i.allowedProductIds ? [...i.allowedProductIds] : [], tenantId: c.tenantId,
      quantity: i.quantity, isOptional: i.isOptional, sortOrder: i.sortOrder,
    })),
  }
}

// ── stub store (PREVIEW_STUB only) ─────────────────────────────────────────
const now = () => new Date().toISOString()
const sid = (p: string) => `${p}-${Math.random().toString(36).slice(2, 8)}`
const stub: { combos: Combo[] } = {
  combos: [
    {
      id: 'cmb-savis-1', tenantId: 'tnt_demo', name: 'Desayuno completo',
      description: 'Café + concha + jugo', priceCents: 9500, pricingMode: 'FIXED_PRICE',
      categoryId: null, imageUrl: null, validFrom: null, validUntil: null, active: true,
      createdAt: now(), updatedAt: now(), deletedAt: null,
      items: [
        { id: 'ci-1', comboId: 'cmb-savis-1', productId: 'p-cafe', categoryId: null, allowedProductIds: [], tenantId: 'tnt_demo', quantity: 1, isOptional: false, sortOrder: 0 },
        { id: 'ci-2', comboId: 'cmb-savis-1', productId: 'p-concha', categoryId: null, allowedProductIds: [], tenantId: 'tnt_demo', quantity: 1, isOptional: false, sortOrder: 1 },
      ],
    },
    {
      id: 'cmb-savis-2', tenantId: 'tnt_demo', name: 'Molletes con queso',
      description: null, priceCents: 7500, pricingMode: 'FIXED_PRICE',
      categoryId: null, imageUrl: null, validFrom: null, validUntil: null, active: true,
      createdAt: now(), updatedAt: now(), deletedAt: null, items: [],
    },
    {
      id: 'cmb-savis-3', tenantId: 'tnt_demo', name: 'Torta',
      description: null, priceCents: 6500, pricingMode: 'FIXED_PRICE',
      categoryId: null, imageUrl: null, validFrom: null, validUntil: null, active: true,
      createdAt: now(), updatedAt: now(), deletedAt: null, items: [],
    },
  ],
}

// ── list / get ─────────────────────────────────────────────────────────────
export async function listCombos(
  params: { q?: string; activeOnly?: boolean } = {},
  signal?: AbortSignal,
): Promise<Combo[]> {
  if (PREVIEW_STUB) {
    let rows = stub.combos
    if (params.q) {
      const q = params.q.toLowerCase()
      rows = rows.filter((c) => c.name.toLowerCase().includes(q))
    }
    if (params.activeOnly) rows = rows.filter((c) => c.active)
    return mock(rows)
  }
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient
    .query(AdminCombosQuery, { limit: 100, ...(params.q ? { q: params.q } : {}) }, ctx)
    .toPromise()
  if (res.error) throw res.error
  const items = (res.data?.combos.items ?? []).map(toUiCombo)
  return params.activeOnly ? items.filter((c) => c.active) : items
}

export async function getCombo(id: string, signal?: AbortSignal): Promise<Combo> {
  if (PREVIEW_STUB) {
    const c = stub.combos.find((x) => x.id === id)
    if (!c) throw new Error('Combo no encontrado')
    return mock(c)
  }
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminComboQuery, { id }, ctx).toPromise()
  if (res.error) throw res.error
  const c = res.data?.combo
  if (!c) throw new Error('Combo no encontrado')
  return toUiCombo(c)
}

// ── create / update / delete ───────────────────────────────────────────────
export async function createCombo(input: {
  name: string
  description?: string
  priceCents: number
  pricingMode?: ComboPricingMode
  categoryId?: string | null
  validFrom?: string | null
  validUntil?: string | null
}): Promise<Combo> {
  if (PREVIEW_STUB) {
    const c: Combo = {
      id: sid('cmb'), tenantId: authTenantId() || 'tnt_demo',
      name: input.name, description: input.description ?? null,
      priceCents: input.priceCents, pricingMode: input.pricingMode ?? 'FIXED_PRICE',
      categoryId: input.categoryId ?? null, imageUrl: null,
      validFrom: input.validFrom ?? null, validUntil: input.validUntil ?? null, active: true,
      createdAt: now(), updatedAt: now(), deletedAt: null, items: [],
    }
    stub.combos.push(c)
    return mock(c)
  }
  // F9.4 / ADR-0009: alta por el grafo (tenant del JWT, RLS). Sin tenantId en el input.
  const res = await gqlClient.mutation(AdminCreateComboMutation, {
    input: {
      name: input.name,
      priceCents: input.priceCents,
      ...(input.description ? { description: input.description } : {}),
      ...(input.pricingMode ? { pricingMode: input.pricingMode } : {}),
      ...(input.categoryId ? { categoryId: input.categoryId } : {}),
      ...(input.validFrom ? { validFrom: input.validFrom } : {}),
      ...(input.validUntil ? { validUntil: input.validUntil } : {}),
    },
  }).toPromise()
  if (res.error) throw res.error
  const c = res.data?.createCombo
  if (!c) throw new Error('createCombo: empty response')
  return toUiCombo(c)
}

export async function updateCombo(id: string, patch: {
  name?: string
  description?: string | null
  priceCents?: number
  active?: boolean
  pricingMode?: ComboPricingMode
  categoryId?: string | null
  imageUrl?: string | null
  validFrom?: string | null
  validUntil?: string | null
}): Promise<Combo> {
  if (PREVIEW_STUB) {
    const c = stub.combos.find((x) => x.id === id)
    if (!c) throw new Error('Combo no encontrado')
    if (patch.name !== undefined) c.name = patch.name
    if (patch.description !== undefined) c.description = patch.description
    if (patch.priceCents !== undefined) c.priceCents = patch.priceCents
    if (patch.active !== undefined) c.active = patch.active
    if (patch.pricingMode !== undefined) c.pricingMode = patch.pricingMode
    if (patch.categoryId !== undefined) c.categoryId = patch.categoryId
    if (patch.imageUrl !== undefined) c.imageUrl = patch.imageUrl
    if (patch.validFrom !== undefined) c.validFrom = patch.validFrom
    if (patch.validUntil !== undefined) c.validUntil = patch.validUntil
    c.updatedAt = now()
    return mock(c)
  }
  const res = await gqlClient.mutation(AdminUpdateComboMutation, {
    id,
    input: {
      ...(patch.name !== undefined ? { name: patch.name } : {}),
      ...(patch.description !== undefined ? { description: patch.description } : {}),
      ...(patch.priceCents !== undefined ? { priceCents: patch.priceCents } : {}),
      ...(patch.active !== undefined ? { active: patch.active } : {}),
      ...(patch.pricingMode !== undefined ? { pricingMode: patch.pricingMode } : {}),
      ...(patch.categoryId !== undefined ? { categoryId: patch.categoryId } : {}),
      ...(patch.imageUrl !== undefined ? { imageUrl: patch.imageUrl } : {}),
      ...(patch.validFrom !== undefined ? { validFrom: patch.validFrom } : {}),
      ...(patch.validUntil !== undefined ? { validUntil: patch.validUntil } : {}),
    },
  }).toPromise()
  if (res.error) throw res.error
  const c = res.data?.updateCombo
  if (!c) throw new Error('updateCombo: empty response')
  return toUiCombo(c)
}

export async function deleteCombo(id: string): Promise<void> {
  if (PREVIEW_STUB) {
    stub.combos = stub.combos.filter((x) => x.id !== id)
    return mock(undefined)
  }
  const res = await gqlClient.mutation(AdminDeleteComboMutation, { id }).toPromise()
  if (res.error) throw res.error
}

// ── items ──────────────────────────────────────────────────────────────────
export async function addComboItem(comboId: string, input: {
  // A7 §1.6 — XOR: un producto fijo (productId) o un slot por categoría (categoryId
  // + allowedProductIds opcional para acotar qué productos de la categoría aplican).
  productId?: string
  categoryId?: string
  allowedProductIds?: string[]
  quantity?: number
  isOptional?: boolean
}): Promise<ComboItem> {
  if (PREVIEW_STUB) {
    const c = stub.combos.find((x) => x.id === comboId)
    if (!c) throw new Error('Combo no encontrado')
    const item: ComboItem = {
      id: sid('ci'), comboId, productId: input.productId ?? null, categoryId: input.categoryId ?? null,
      allowedProductIds: input.allowedProductIds ?? [], tenantId: c.tenantId,
      quantity: input.quantity ?? 1, isOptional: input.isOptional ?? false,
      sortOrder: c.items.length,
    }
    c.items.push(item)
    c.updatedAt = now()
    return mock(item)
  }
  // XOR — exactamente uno de productId | categoryId.
  const hasProduct = !!input.productId
  const hasCategory = !!input.categoryId
  if (hasProduct === hasCategory) throw new Error('Indica exactamente uno: un producto fijo o una categoría (slot).')
  const res = await gqlClient.mutation(AdminAddComboItemMutation, {
    comboId,
    input: {
      ...(hasProduct ? { productId: input.productId } : {}),
      ...(hasCategory ? { categoryId: input.categoryId } : {}),
      ...(input.allowedProductIds && input.allowedProductIds.length > 0 ? { allowedProductIds: input.allowedProductIds } : {}),
      quantity: input.quantity ?? 1,
      isOptional: input.isOptional ?? false,
    },
  }).toPromise()
  if (res.error) throw res.error
  const it = res.data?.addComboItem
  if (!it) throw new Error('addComboItem: empty response')
  // tenantId no esta en el SDL de ComboItem (denormalizado del combo padre);
  // lo tomamos del JWT para el tipo de UI.
  return { id: it.id, comboId: it.comboId, productId: it.productId ?? null, categoryId: it.categoryId ?? null, allowedProductIds: it.allowedProductIds ? [...it.allowedProductIds] : [], tenantId: authTenantId() ?? '', quantity: it.quantity, isOptional: it.isOptional, sortOrder: it.sortOrder }
}

export async function removeComboItem(comboId: string, itemId: string): Promise<void> {
  if (PREVIEW_STUB) {
    const c = stub.combos.find((x) => x.id === comboId)
    if (c) c.items = c.items.filter((it) => it.id !== itemId)
    return mock(undefined)
  }
  const res = await gqlClient.mutation(AdminRemoveComboItemMutation, { comboId, itemId }).toPromise()
  if (res.error) throw res.error
}

// Editar IN-PLACE un item de combo → grafo catalog-combos `updateComboItem`
// (tenant del JWT, IDOR-guard por combo). Conserva el id del item (a diferencia
// del antiguo remove-then-add). Permite cambiar cantidad/opcional/orden y, con
// XOR, el producto fijo o el slot por categoría (+ allowedProductIds). El `item`
// actual sirve para resolver el XOR cuando el patch no lo redefine.
export async function updateComboItem(
  comboId: string,
  item: ComboItem,
  patch: {
    quantity?: number
    isOptional?: boolean
    sortOrder?: number
    // XOR — si se manda uno, debe excluir al otro. Si no se mandan, se conserva el actual.
    productId?: string | null
    categoryId?: string | null
    allowedProductIds?: string[]
  },
): Promise<void> {
  if (PREVIEW_STUB) {
    const c = stub.combos.find((x) => x.id === comboId)
    const it = c?.items.find((x) => x.id === item.id)
    if (it) {
      if (patch.quantity !== undefined) it.quantity = patch.quantity
      if (patch.isOptional !== undefined) it.isOptional = patch.isOptional
      if (patch.sortOrder !== undefined) it.sortOrder = patch.sortOrder
      if (patch.productId !== undefined) { it.productId = patch.productId; if (patch.productId) it.categoryId = null }
      if (patch.categoryId !== undefined) { it.categoryId = patch.categoryId; if (patch.categoryId) it.productId = null }
      if (patch.allowedProductIds !== undefined) it.allowedProductIds = patch.allowedProductIds
      c!.updatedAt = now()
    }
    return mock(undefined)
  }
  // Resuelve el XOR final (lo del patch tiene prioridad; si no, lo del item actual).
  const nextProductId = patch.productId !== undefined ? patch.productId : item.productId
  const nextCategoryId = patch.categoryId !== undefined ? patch.categoryId : item.categoryId
  const hasProduct = !!nextProductId
  const hasCategory = !!nextCategoryId
  if (hasProduct === hasCategory) throw new Error('Indica exactamente uno: un producto fijo o una categoría (slot).')
  const input: { quantity?: number; isOptional?: boolean; sortOrder?: number; productId?: string; categoryId?: string; allowedProductIds?: string[] } = {}
  if (patch.quantity !== undefined) input.quantity = patch.quantity
  if (patch.isOptional !== undefined) input.isOptional = patch.isOptional
  if (patch.sortOrder !== undefined) input.sortOrder = patch.sortOrder
  // El item queda como producto fijo O slot por categoría (nunca ambos).
  if (hasProduct) input.productId = nextProductId!
  if (hasCategory) input.categoryId = nextCategoryId!
  const allowed = patch.allowedProductIds !== undefined ? patch.allowedProductIds : item.allowedProductIds
  if (hasCategory && allowed.length > 0) input.allowedProductIds = allowed
  const res = await gqlClient.mutation(AdminUpdateComboItemMutation, { comboId, itemId: item.id, input }).toPromise()
  if (res.error) throw res.error
  if (!res.data?.updateComboItem) throw new Error('updateComboItem: empty response')
}
